import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import {
  ChangesPlanningSection,
  type IPlanChangeLog,
} from 'partner360/models/planning/IPlanChangeLog'
import { fetchPlanChangesLog } from 'partner360/services/planning/fetchPlanChangesLog'
import { useNetworkDebugSource } from 'shared/lib/networkDebug'

import { recordAcknowledgmentCall } from '../api/recordAcknowledgmentCall'

import type { AcknowledgmentOffering } from './types'

type UseAcknowledgmentPlanChangesLogParams = {
  show: boolean
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  changeDate?: string
  isDistributor: boolean
}

type PlanChangesGroup = {
  key: string
  category: string
  subCategory: string
  modified: string
  modifiedBy: string
  items: IPlanChangeLog[]
}

const getPlanChangesGroupKey = (category: string, subCategory: string) =>
  `${category}-${subCategory}`

export const useAcknowledgmentPlanChangesLog = ({
  show,
  partnerId,
  fYear,
  programType,
  changeDate,
  isDistributor,
}: UseAcknowledgmentPlanChangesLogParams) => {
  const ownerId = useSelector(selectAppOwnerId)
  const debugSource = useNetworkDebugSource()

  const [logs, setLogs] = useState<IPlanChangeLog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0)

  useEffect(() => {
    if (!show || !partnerId || !fYear || programType === '' || !ownerId) {
      setLogs([])
      setIsLoading(false)

      return
    }

    let isCancelled = false

    const load = async () => {
      setIsLoading(true)

      const params = {
        partnerId,
        changeDate,
        fYear,
        programType: programType as ProgramTypes | null,
        isDistributor,
        ownerId,
      }
      /* The service returns the procedure's `Data` only, so that is what the window shows. */
      const response = await recordAcknowledgmentCall(
        {
          name: 'spJBPPlanChangesLog',
          method: 'POST',
          url: '/webservice/197/sp/v4/spJBPPlanChangesLog/call',
          body: { partnerId, fYear, programType, changeDate, isDistributor },
          target: 'useAcknowledgmentPlanChangesLog · logs',
          source: debugSource,
        },
        () => fetchPlanChangesLog(params)
      )

      if (!isCancelled) {
        setLogs(response)
        setIsLoading(false)
      }
    }

    void load()

    return () => {
      isCancelled = true
    }
  }, [changeDate, debugSource, fYear, isDistributor, ownerId, partnerId, programType, show])

  const groups = useMemo<PlanChangesGroup[]>(() => {
    const groupsMap = new Map<string, PlanChangesGroup>()

    logs.forEach((log) => {
      const key = getPlanChangesGroupKey(log.Category, log.SubCategory)
      const existingGroup = groupsMap.get(key)

      if (existingGroup) {
        existingGroup.items.push(log)

        return
      }

      groupsMap.set(key, {
        key,
        category: log.Category,
        subCategory: log.SubCategory,
        modified: log.Modified,
        modifiedBy: log.ModifiedBy,
        items: [log],
      })
    })

    return Array.from(groupsMap.values())
  }, [logs])

  useEffect(() => {
    setSelectedGroupIndex((currentIndex) => {
      if (groups.length === 0) {
        return 0
      }

      return Math.min(currentIndex, groups.length - 1)
    })
  }, [groups.length])

  const selectedGroup = groups[selectedGroupIndex] ?? null

  const sections = useMemo<Partial<Record<ChangesPlanningSection, IPlanChangeLog>>>(() => {
    if (!selectedGroup) {
      return {}
    }

    return selectedGroup.items.reduce<Partial<Record<ChangesPlanningSection, IPlanChangeLog>>>(
      (acc, item) => {
        acc[item.PlanningSection] = item

        return acc
      },
      {}
    )
  }, [selectedGroup])

  return {
    isLoading,
    groups,
    selectedGroupIndex,
    setSelectedGroupIndex,
    selectedGroup,
    sections,
  }
}
